import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "../api";
import { useLang } from "../i18n";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Presentation() {
  const { t, lang } = useLang();
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const bio = content ? (lang === "fr" ? content.bio_fr : content.bio_en) : "";

  return (
    <div data-testid="presentation-page" className="pt-16">
      {/* Hero / bio */}
      <section className="px-6 sm:px-12 lg:px-20 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <motion.div variants={fadeUp} initial="hidden" animate="show" className="lg:col-span-4">
          <p className="font-mono-meta text-xs uppercase tracking-[0.25em] text-[#C83E2B] mb-6">{t("pres.bio_kicker")}</p>
          <h1 className="font-serif-display font-light text-6xl lg:text-7xl leading-[0.95]" data-testid="presentation-title">
            {t("pres.title")}
          </h1>
          <p className="mt-6 font-mono-meta text-xs uppercase tracking-[0.2em] text-[#8C8983]">
            {lang === "fr" ? "Vit et travaille à Toulouse" : "Lives and works in Toulouse"}
          </p>
          <div className="mt-12 p-3 bg-white frame-shadow max-w-xs">
            <img src="/artworks/max-atelier.jpg" alt="Max Dhum dans son atelier" className="w-full aspect-[4/5] object-cover" data-testid="artist-portrait" />
            <p className="pt-3 px-1 pb-1 font-mono-meta text-[10px] uppercase tracking-[0.2em] text-[#8C8983]">
              {lang === "fr" ? "Dans l'atelier · Toulouse" : "In the studio · Toulouse"}
            </p>
          </div>
        </motion.div>
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="show"
          transition={{ delay: 0.15 }}
          className="lg:col-span-6 lg:col-start-6 flex flex-col justify-center"
        >
          {bio.split("\n\n").map((p, i) => (
            <p key={i} className="text-base lg:text-lg text-[#5A5853] leading-relaxed mb-6 first:text-xl first:lg:text-2xl first:font-serif-display first:text-[#121212] first:leading-snug" data-testid={`bio-paragraph-${i}`}>
              {p}
            </p>
          ))}
        </motion.div>
      </section>

      {/* Pillars */}
      <section className="border-y" style={{ borderColor: "#E5E2DC", background: "#F3F1EC" }} data-testid="pillars-section">
        <div className="px-6 sm:px-12 lg:px-20 py-24">
          <p className="font-mono-meta text-xs uppercase tracking-[0.25em] text-[#C83E2B] mb-14">{t("pres.pillars_kicker")}</p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-14">
            {(content?.pillars || []).map((p, i) => (
              <motion.article
                key={p.key}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.12, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`pillar-${p.key}`}
              >
                <span className="font-serif-display italic text-5xl text-[#C5C0B7]">0{i + 1}</span>
                <h2 className="font-serif-display text-3xl mt-4 mb-6">{lang === "fr" ? p.title_fr : p.title_en}</h2>
                <p className="text-sm text-[#5A5853] leading-relaxed">{lang === "fr" ? p.text_fr : p.text_en}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Scénographie — propositions d'accrochage, sans prix */}
      {content?.scenography && (
        <section className="px-6 sm:px-12 lg:px-20 py-24 border-b" style={{ borderColor: "#E5E2DC" }} data-testid="scenography-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-4">
              <p className="font-mono-meta text-xs uppercase tracking-[0.25em] text-[#C83E2B] mb-4">Scénographie</p>
              <h2 className="font-serif-display font-light text-4xl sm:text-5xl leading-tight">
                {lang === "fr" ? "L'œuvre exposée" : "The work, exhibited"}
              </h2>
            </div>
            <p className="lg:col-span-6 lg:col-start-7 text-base text-[#5A5853] leading-relaxed" data-testid="scenography-text">
              {lang === "fr" ? content.scenography.text_fr : content.scenography.text_en}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {content.scenography.images.map((src, i) => (
              <motion.div
                key={src}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: (i % 2) * 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="overflow-hidden"
                data-testid={`scenography-image-${i}`}
              >
                <img
                  src={src}
                  alt={lang === "fr" ? `Proposition d'accrochage ${i + 1}` : `Hanging proposal ${i + 1}`}
                  loading="lazy"
                  className="w-full object-cover hover:scale-[1.03] transition-transform duration-700 aspect-[1.414]"
                />
              </motion.div>
            ))}
          </div>
          <p className="mt-4 font-mono-meta text-[10px] uppercase tracking-[0.2em] text-[#8C8983]">
            {lang === "fr"
              ? "Propositions visuelles d'accrochage — « Vestiges », projection vidéo 5:00 min, 2025"
              : "Visual hanging proposals — “Vestiges”, video projection 5:00 min, 2025"}
          </p>
        </section>
      )}

      {/* Atelier strip */}
      <section className="px-6 sm:px-12 lg:px-20 py-24" data-testid="atelier-section">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {["/artworks/p5.jpg", "/artworks/p6.jpg", "/artworks/p39.jpg"].map((src, i) => (
            <motion.div
              key={src}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.7 }}
              className={`overflow-hidden ${i === 1 ? "sm:mt-12" : ""}`}
            >
              <img src={src} alt={t("pres.atelier")} loading="lazy" className="w-full aspect-[4/3] object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
          ))}
        </div>
        <p className="mt-4 font-mono-meta text-[10px] uppercase tracking-[0.2em] text-[#8C8983]">{t("pres.atelier")}</p>
      </section>

      {/* Exhibitions */}
      <section className="px-6 sm:px-12 lg:px-20 pb-28" data-testid="exhibitions-section">
        <p className="font-mono-meta text-xs uppercase tracking-[0.25em] text-[#C83E2B] mb-4">{t("pres.expo_kicker")}</p>
        <h2 className="font-serif-display font-light text-4xl sm:text-5xl mb-14">{t("pres.expo_title")}</h2>
        <div className="border-t" style={{ borderColor: "#E5E2DC" }}>
          {(content?.exhibitions || []).map((e, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-[90px_1fr] sm:grid-cols-[140px_1fr] gap-6 py-4 border-b items-baseline"
              style={{ borderColor: "#E5E2DC" }}
              data-testid={`exhibition-${i}`}
            >
              <span className="font-mono-meta text-xs tracking-[0.1em] text-[#C83E2B]">{e.year}</span>
              <span className="text-sm sm:text-base text-[#121212]">{lang === "fr" ? e.text_fr : e.text_en}</span>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
